import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Megaphone,
  Phone,
  ShieldAlert,
  CircleSlash,
  AlertTriangle,
  CheckCircle2,
  Bot,
  ExternalLink,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import DispositionTag from "../components/DispositionTag";
import { toast } from "sonner";

const STATUS_META = {
  called: { label: "Called", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", icon: Phone },
  received: { label: "Received", color: "text-amber-400 bg-amber-500/10 border-amber-500/30", icon: AlertTriangle },
  honeypot_caught: { label: "Honeypot", color: "text-zinc-500 bg-zinc-500/10 border-zinc-500/30", icon: Bot },
  rate_limited_ip: { label: "Rate Limit (IP)", color: "text-orange-400 bg-orange-500/10 border-orange-500/30", icon: CircleSlash },
  rate_limited_phone: { label: "Rate Limit (phone)", color: "text-orange-400 bg-orange-500/10 border-orange-500/30", icon: CircleSlash },
  invalid_phone: { label: "Invalid phone", color: "text-red-400 bg-red-500/10 border-red-500/30", icon: ShieldAlert },
  dnc_blocked: { label: "DNC blocked", color: "text-red-500 bg-red-500/15 border-red-500/30", icon: ShieldAlert },
  captcha_failed: { label: "Captcha failed", color: "text-red-400 bg-red-500/10 border-red-500/30", icon: ShieldAlert },
  no_consent: { label: "No consent", color: "text-red-400 bg-red-500/10 border-red-500/30", icon: ShieldAlert },
  provider_failed: { label: "Provider error", color: "text-orange-500 bg-orange-500/10 border-orange-500/30", icon: AlertTriangle },
  no_phone_number: { label: "No phone configured", color: "text-orange-500 bg-orange-500/10 border-orange-500/30", icon: AlertTriangle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status,
    color: "text-white/50 bg-white/5 border-white/10",
    icon: AlertTriangle,
  };
  const Icon = meta.icon;
  return (
    <span
      data-testid={`consent-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${meta.color}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const Stat = ({ label, value, color = "text-white", testid }) => (
  <div data-testid={testid} className="bg-[#121214] border border-white/10 rounded-lg p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
    <div className={`font-mono text-2xl mt-1 tracking-tighter ${color}`}>{value}</div>
  </div>
);

export default function Consent() {
  const [data, setData] = useState({ items: [], by_status: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/consent");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const s = data.by_status || {};
  const called = s.called || 0;
  const blocked = (s.dnc_blocked || 0) + (s.invalid_phone || 0) + (s.no_consent || 0);
  const ratelimited = (s.rate_limited_ip || 0) + (s.rate_limited_phone || 0);
  const failed = (s.provider_failed || 0) + (s.vapi_failed || 0) + (s.no_phone_number || 0);
  const honeypot = s.honeypot_caught || 0;

  const [resettingPhone, setResettingPhone] = useState(null);
  const [manualPhone, setManualPhone] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualIp, setManualIp] = useState("");
  const [manualIpSubmitting, setManualIpSubmitting] = useState(false);

  const resetCooldownFor = async (phone, label) => {
    if (!phone) {
      toast.error("No phone on this row");
      return;
    }
    setResettingPhone(phone);
    try {
      const { data } = await api.post("/admin/cooldown/reset", { phone });
      const cleared = (data?.deleted?.phone_keys || 0) + (data?.deleted?.otp_records || 0);
      toast.success(`Cooldown reset for ${label || phone} — cleared ${cleared} record${cleared === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResettingPhone(null);
    }
  };

  const submitManualReset = async (e) => {
    e.preventDefault();
    if (!manualPhone.trim()) return;
    setManualSubmitting(true);
    try {
      const { data } = await api.post("/admin/cooldown/reset", { phone: manualPhone.trim() });
      const cleared = (data?.deleted?.phone_keys || 0) + (data?.deleted?.otp_records || 0);
      toast.success(`Cooldown reset for ${manualPhone} — cleared ${cleared} record${cleared === 1 ? "" : "s"}`);
      setManualPhone("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setManualSubmitting(false);
    }
  };

  const submitIpReset = async (e) => {
    e.preventDefault();
    const ip = manualIp.trim();
    if (!ip) return;
    setManualIpSubmitting(true);
    try {
      const { data } = await api.post("/admin/cooldown/reset", { ip });
      const cleared = data?.deleted?.ip_keys || 0;
      toast.success(`IP ${ip} reset — cleared ${cleared} record${cleared === 1 ? "" : "s"}`);
      setManualIp("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setManualIpSubmitting(false);
    }
  };

  return (
    <div data-testid="consent-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
          Public Form
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-white">
          Hear Ashley — Consent Submissions
        </h1>
        <p className="text-sm text-white/50 mt-2">
          Every submission to <span className="font-mono">repliantarc.com/ashley</span> with full
          audit trail (IP, user agent, consent text). Successful submissions trigger Ashley to
          dial the number immediately, bypassing the campaign window.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat testid="stat-total" label="Total" value={data.total} />
        <Stat testid="stat-called" label="Called" value={called} color="text-emerald-400" />
        <Stat testid="stat-blocked" label="Blocked" value={blocked} color="text-red-400" />
        <Stat testid="stat-ratelimited" label="Rate-limited" value={ratelimited} color="text-orange-400" />
        <Stat testid="stat-honeypot" label="Honeypot" value={honeypot} color="text-zinc-400" />
      </div>

      {failed > 0 && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-md p-3 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">{failed} submission{failed > 1 ? "s" : ""} failed to dial.</div>
            <div className="text-xs text-red-300/80 mt-1">
              Check your calling provider's account status and that a phone number is provisioned. Affected
              submissions are marked below.
            </div>
          </div>
        </div>
      )}

      {/* Cooldown reset (testing / support tool) */}
      <div className="mb-3 bg-[#121214] border border-white/5 rounded-xl p-4" data-testid="cooldown-reset-bar">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-shrink-0">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#FFB000] font-medium">Testing tool</div>
            <div className="text-sm text-white font-medium mt-0.5">Reset 24h cooldown for a phone</div>
          </div>
          <form onSubmit={submitManualReset} className="flex-1 flex gap-2 items-stretch w-full md:w-auto md:ml-auto">
            <input
              type="tel"
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              data-testid="cooldown-reset-phone-input"
              className="flex-1 bg-black/30 border border-white/10 rounded-md text-white text-sm px-3 py-2 placeholder:text-white/30 focus:outline-none focus:border-[#FFB000]"
            />
            <button
              type="submit"
              disabled={manualSubmitting || !manualPhone.trim()}
              data-testid="cooldown-reset-submit-btn"
              className="px-4 py-2 rounded-md bg-[#FFB000] text-black text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
            >
              {manualSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Reset
            </button>
          </form>
        </div>
        <div className="text-[11px] text-white/40 mt-2">
          Clears the consent rate-limit + OTP records for this phone. Use it to re-test the public form without waiting 24 hours.
        </div>
      </div>

      {/* IP rate-limit reset (testing / support tool) */}
      <div className="mb-6 bg-[#121214] border border-white/5 rounded-xl p-4" data-testid="ip-reset-bar">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-shrink-0">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#FFB000] font-medium">Testing tool</div>
            <div className="text-sm text-white font-medium mt-0.5">Reset hourly IP rate-limit</div>
          </div>
          <form onSubmit={submitIpReset} className="flex-1 flex gap-2 items-stretch w-full md:w-auto md:ml-auto">
            <input
              type="text"
              value={manualIp}
              onChange={(e) => setManualIp(e.target.value)}
              placeholder="72.186.232.155"
              data-testid="ip-reset-input"
              className="flex-1 bg-black/30 border border-white/10 rounded-md text-white text-sm px-3 py-2 placeholder:text-white/30 focus:outline-none focus:border-[#FFB000] font-mono"
            />
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await fetch("https://api.ipify.org?format=json");
                  const j = await r.json();
                  setManualIp(j.ip || "");
                } catch {
                  toast.error("Couldn't detect your IP — paste it manually");
                }
              }}
              data-testid="ip-reset-fillme-btn"
              className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-white/70 text-xs font-medium border border-white/10 whitespace-nowrap"
              title="Auto-fill with your current public IP"
            >
              Use my IP
            </button>
            <button
              type="submit"
              disabled={manualIpSubmitting || !manualIp.trim()}
              data-testid="ip-reset-submit-btn"
              className="px-4 py-2 rounded-md bg-[#FFB000] text-black text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
            >
              {manualIpSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Reset
            </button>
          </form>
        </div>
        <div className="text-[11px] text-white/40 mt-2">
          Clears the per-hour IP rate-limit (consent + booking + preview). Use when "Too many requests" is blocking your end-to-end testing.
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <Megaphone className="w-4 h-4 text-[#FFB000]" />
          <div className="text-sm font-medium text-white">Recent submissions</div>
          {loading && <div className="text-xs font-mono text-white/40">loading…</div>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consent-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <th className="text-left px-5 py-3 font-normal">When</th>
                <th className="text-left px-5 py-3 font-normal">Name</th>
                <th className="text-left px-5 py-3 font-normal">Phone</th>
                <th className="text-left px-5 py-3 font-normal">Email / Company</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
                <th className="text-left px-5 py-3 font-normal">Disposition</th>
                <th className="text-left px-5 py-3 font-normal">Audit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-white/40 text-sm">
                    No submissions yet. Once your form goes live, every entry lands here.
                  </td>
                </tr>
              )}
              {data.items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`consent-row-${it.id}`}
                  className={`hover:bg-white/[0.02] ${it.status !== "called" ? "opacity-90" : ""}`}
                >
                  <td className="px-5 py-3 align-top text-white/70 font-mono text-xs whitespace-nowrap">
                    {it.created_at && (
                      <div title={format(parseISO(it.created_at), "yyyy-MM-dd HH:mm:ss")}>
                        {formatDistanceToNow(parseISO(it.created_at), { addSuffix: true })}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 align-top text-white/90">{it.name}</td>
                  <td className="px-5 py-3 align-top font-mono text-white/80">
                    <div className="flex items-center gap-2">
                      <span>{it.phone || it.phone_raw}</span>
                      {(it.phone || it.phone_raw) && (
                        <button
                          type="button"
                          onClick={() => resetCooldownFor(it.phone || it.phone_raw, it.name)}
                          disabled={resettingPhone === (it.phone || it.phone_raw)}
                          data-testid={`reset-cooldown-${it.id}`}
                          title="Reset 24h cooldown for this number"
                          className="text-white/30 hover:text-[#FFB000] transition-colors disabled:opacity-50"
                        >
                          {resettingPhone === (it.phone || it.phone_raw)
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <RotateCcw className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 align-top text-white/60 text-xs">
                    <div>{it.email || "—"}</div>
                    {it.company && <div className="text-white/40 mt-0.5">{it.company}</div>}
                  </td>
                  <td className="px-5 py-3 align-top">
                    <StatusPill status={it.status} />
                    {it.error && (
                      <div className="mt-1 text-[10px] font-mono text-red-400/80 max-w-[180px] truncate" title={it.error}>
                        {it.error}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 align-top">
                    {it.call ? (
                      <div className="space-y-1">
                        <DispositionTag value={it.call.disposition || it.call.ai_disposition} />
                        <div className="text-[10px] font-mono text-white/40">
                          {it.call.duration_seconds ? `${it.call.duration_seconds}s · ` : ""}
                          {it.call.status}
                        </div>
                      </div>
                    ) : (
                      <span className="text-white/30 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 align-top text-[10px] font-mono text-white/40 max-w-[220px]">
                    <div className="truncate" title={it.ip}>IP: {it.ip || "—"}</div>
                    <div className="truncate" title={it.user_agent}>UA: {(it.user_agent || "—").slice(0, 28)}</div>
                    {it.source_url && (
                      <a
                        href={it.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#FFB000] hover:underline mt-1"
                      >
                        source <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {it.call_id && (
                      <button
                        data-testid={`consent-open-call-${it.id}`}
                        onClick={() => nav(`/calls?open=${it.call_id}`)}
                        className="inline-flex items-center gap-1 text-emerald-400 hover:underline mt-1 block"
                      >
                        open call <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-[#0e0e10] border border-white/10 rounded-xl p-5 text-xs font-mono text-white/60 leading-relaxed">
        <div className="flex items-center gap-2 text-white/80 mb-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          <span className="uppercase tracking-widest text-[10px]">Endpoint live at</span>
        </div>
        <code className="text-[#FFB000]">POST /api/consent/hear-ashley</code>
        <div className="mt-2">
          Body: <code>{`{ name, phone, email?, company?, consent: true, captcha_token?, source_url?, hp_field: "" }`}</code>
        </div>
        <div className="mt-2 text-white/40">
          Honeypot field name is <code>hp_field</code> — render it visually hidden, leave empty.
          Bots fill it; humans don't. CORS is open to all origins so you can post from
          repliantarc.com directly.
        </div>
      </div>
    </div>
  );
}
